// eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-imports-ts
import { Component, Prop, State, Watch, h, Env } from '@stencil/core';

import { AffiliateControllerResponse } from '../../../../types/controllers/affiliate';
import { DeviceType, FormattedPromotion } from '../../components';
import { formatPromotions } from '../../utils';
import { ageClass } from '../../utils/age-check';
import { getPlacement } from '../../utils/api';
import { DEFAULT_ROTATE_TIME } from '../../utils/image';

@Component({
    tag: 'banner-component',
    styleUrl: 'banner-component.css',
    shadow: true,
})
export class BannerComponent {
    private defaultCallback() {
        return null;
    }

    // props
    @Prop() placementId: number;
    @Prop() propertyId: number;
    @Prop() context: string;
    @Prop() callback: () => void = this.defaultCallback;

    // optional characteristics props
    @Prop() location: string;
    @Prop() pageSlug: string;
    @Prop() subscriptionTier: 0 | 1 | 2 | 3;
    @Prop() wasPro: boolean;
    @Prop() subdivision: number;
    @Prop() league: string;
    @Prop() gameId: number;
    @Prop() ctaType: string;
    @Prop() isMobile: boolean;
    @Prop() deviceType: DeviceType;
    @Prop() isBetsyncUser: boolean;
    @Prop() userParentBookIds: string;
    @Prop() isLoggedIn: boolean;
    @Prop() sponsorSlug: string;

    // optional props for functionality
    @Prop() defer: boolean;
    @Prop() devMode = false;
    @Prop() rotateTime = DEFAULT_ROTATE_TIME;
    @Prop() rotate = true;
    @Prop() age: number;
    @Prop() hideUnderage = false;
    @Prop() terms: 'geo' | 'affiliate' = 'geo';

    // state
    @State() promotions: Array<FormattedPromotion> = [
        {
            id: 1,
            link: '',
            secondaryLink: null,
            buttonText: '',
            logo: 'https://assets.actionnetwork.com/816137_square-xxl.png',
            largeLogo: 'https://assets.actionnetwork.com/816137_square-xxl.png',
            bookName: 'Placeholder',
            bookDataName: 'Placeholder',
            bookType: '',
            logoColor: 'cccccc',
            cta: 'Turn your sports knowledge into winning predictions',
            impressionUrls: [],
            termsText:
                '21+ or 18+ in Certain Locations. 19+ in ON. Please Play Responsibly. Gambling Problem? Call 1-800-GAMBLER. Visit connexontario.ca or Call 1-866-531-2600 in ON.',
            bonusCode: '',
            props: [],
            bookId: 1,
            campaignId: 1,
            promotionListId: 1,
            images: [],
            valueProps: [],
            isQuickslip: false,
            internalIdentifiers: [],
            minAge: 18,
            affiliateTerms: '',
            impressionTtl: 86400,
            impressionThrottle: 1,
            impressionThrottleClick: 1,
            affiliateId: 0,
            rating: 5,
        },
    ];
    @State() ageFound: number;

    @Watch('defer')
    onDeferChange(newDeferValue) {
        if (!newDeferValue) {
            getPlacement(
                {
                    placement_id: this.placementId,
                    property_id: this.propertyId,
                    location: this.location,
                    page_slug: this.pageSlug,
                    subscription_tier: this.subscriptionTier,
                    was_pro: this.wasPro,
                    subdivision_id: this.subdivision,
                    league: this.league,
                    game_id: this.gameId,
                    cta_type: this.ctaType,
                    is_mobile: this.isMobile,
                    device_type: this.deviceType,
                    isBetsyncUser: this.isBetsyncUser,
                    user_parent_book_ids: this.userParentBookIds ? this.userParentBookIds.split(',').map(Number) : [],
                    is_logged_in: this.isLoggedIn,
                    sponsor_slug: this.sponsorSlug,
                    context: this.context,
                },
                this.setNewPromosToState.bind(this),
                this.devMode,
                'Sticky CTA'
            );
        }
    }

    async componentWillLoad() {
        if (!this.defer && Env.BAM_HYDRATE !== 'true') {
            getPlacement(
                {
                    placement_id: this.placementId,
                    property_id: this.propertyId,
                    location: this.location,
                    page_slug: this.pageSlug,
                    subscription_tier: this.subscriptionTier,
                    was_pro: this.wasPro,
                    subdivision_id: this.subdivision,
                    league: this.league,
                    game_id: this.gameId,
                    cta_type: this.ctaType,
                    is_mobile: this.isMobile,
                    device_type: this.deviceType,
                    isBetsyncUser: this.isBetsyncUser,
                    user_parent_book_ids: this.userParentBookIds ? this.userParentBookIds.split(',').map(Number) : [],
                    is_logged_in: this.isLoggedIn,
                    sponsor_slug: this.sponsorSlug,
                    context: this.context,
                },
                this.setNewPromosToState.bind(this),
                this.devMode,
                'Banner Component'
            );
        }
        if (Env.BAM_HYDRATE === 'true') {
            await getPlacement(
                {
                    placement_id: this.placementId,
                    property_id: this.propertyId,
                    location: this.location,
                    page_slug: this.pageSlug,
                    subscription_tier: this.subscriptionTier,
                    was_pro: this.wasPro,
                    subdivision_id: this.subdivision,
                    league: this.league,
                    game_id: this.gameId,
                    cta_type: this.ctaType,
                    is_mobile: this.isMobile,
                    device_type: this.deviceType,
                    isBetsyncUser: this.isBetsyncUser,
                    user_parent_book_ids: this.userParentBookIds ? this.userParentBookIds.split(',').map(Number) : [],
                    is_logged_in: this.isLoggedIn,
                    sponsor_slug: this.sponsorSlug,
                    context: this.context,
                },
                this.setNewPromosToState.bind(this),
                this.devMode,
                'Banner Component'
            );
        }
    }

    setNewPromosToState(placement: AffiliateControllerResponse, location: string) {
        const promotions = formatPromotions(placement.promotions, {
            allowAltLink: placement.allow_alternate_link,
            promotionListId: placement.id,
            location: location,
            context: this.context,
            propertyId: this.propertyId,
        });

        if (!promotions.length) {
            // eslint-disable-next-line no-console
            console.warn('BAM Promotion Sticky CTA: No active or valid promotions returned');
            this.promotions = [];
            return;
        }
        this.promotions = promotions;
    }

    render() {
        const promotion = this.promotions[0];

        const eventOptions = {
            placementId: this.placementId,
            allPromotions: this.promotions,
            league: this.league,
            pageSlug: this.pageSlug,
        };

        return !this.defer && this.promotions.length ? (
            this.promotions.length > 1 && this.rotate ? (
                <div class={ageClass(this.age, this.promotions, this.hideUnderage)}>
                    <rotating-banner-component
                        promotions={this.promotions}
                        propertyId={this.propertyId}
                        context={this.context}
                        rotateTime={this.rotateTime}
                        location={this.location}
                        eventOptions={eventOptions}
                        terms={this.terms}
                        callback={this.callback}
                    ></rotating-banner-component>
                </div>
            ) : (
                <div class={ageClass(this.age, this.promotions, this.hideUnderage)}>
                    <static-banner-component
                        eventOptions={eventOptions}
                        promotion={promotion}
                        terms={this.terms}
                        callback={this.callback}
                    ></static-banner-component>
                </div>
            )
        ) : (
            <div class={ageClass(this.age, this.promotions, this.hideUnderage)}>
                <static-banner-component
                    eventOptions={eventOptions}
                    promotion={promotion}
                    terms={this.terms}
                    callback={this.callback}
                ></static-banner-component>
            </div>
        );
    }
}
